<template>
  <div>
    <configurar-associacao-fila-formulario-dialog
      v-if="associacaoSelecionada"
      :is-configurar-associacao-fila-formulario-dialog-open.sync="isConfigurarAssociacaoFilaFormularioDialogOpen"
      :associacao-data="associacaoSelecionada"
      :ordens-permitidas="ordensPermitidas"
      @refetch-data="$emit('refetch-data')"
    />

    <ativar-ou-inativar-associacao-dialog
      :is-ativar-ou-inativar-associacao-dialog-open.sync="isAtivarOuInativarAssociacaoFilaFormularioDialogOpen"
      :is-associacao-ativa-mensagem="isAssociacaoAtivaMensagem"
      :loading="loading"
      :close-dialog="closeDialogAssociarOuDesassociar"
      :confirmar-ativacao-ou-inativacao-da-associacao="confirmarAtivacaoOuInativacaoDaAssociacao"
    />

    <v-data-table
      hide-default-footer
      :headers="headers"
      :items="items"
      :acesso-disable="acessoDisable"
      :item-class="cursorPointer"
      @click:row="selectAssociacao"
    >
      <template #[`item.nome`]="{ item }">
        <div class="d-flex align-center">
          <v-avatar
            :color="`${$vuetify.theme.dark ? 'primary' : '#DCDCDC'}`"
            size="32"
          >
            <span :class="`${$vuetify.theme.dark ? 'white--text' : 'primary--text'} font-weight-bold`">
              {{ item.formularioNome.toUpperCase().charAt(0) }}
            </span>
          </v-avatar>
          <div class="d-flex flex-column ms-3">
            {{ item.formularioNome }}
          </div>
        </div>
      </template>

      <template #[`item.descricao`]="{ item }">
        <span>
          {{ item.formularioDescricao }}
        </span>
      </template>

      <template #[`item.ativo`]="{ item }">
        <v-chip
          v-if="item.ativo !== null"
          class="v-chip-light-bg font-weight-semibold text-capitalize"
          small
          :class="`${item.ativo ? 'success--text' : 'secondary--text'}`"
          :color="`${item.ativo ? 'success' : 'secondary'}`"
        >
          {{ item.ativo ? "Ativa" : "Inativa" }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          v-if="item.ativo !== null"
          bottom
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              class="ml-n5"
              color="primary"
              v-on="on"
            >
              <v-icon @click="ativarOuInativarAssociacaoItem(item)">
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>

          <v-list v-if="acessoDisable">
            <v-list-item>
              <v-list-item-title
                role="button"
                class="d-flex align-end"
                @click="openDialogAssociarOuDesassociar"
              >
                <v-icon
                  size="20"
                  class="mr-1"
                >
                  {{ isAssociacaoAtiva ? icons.mdiCloseThick : icons.mdiCheckBold }}
                </v-icon>
                <span class="text-capitalize"> {{ isAssociacaoAtivaMensagem }} </span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { mdiCheckBold, mdiCloseThick, mdiDotsVertical } from '@mdi/js'
import AtivarOuInativarAssociacaoDialog from '@/views/ativar-ou-inativar-associacao-dialog/AtivarOuInativarAssociacaoDialog.vue'
import ConfigurarAssociacaoFilaFormularioDialog from './ConfigurarAssociacaoFilaFormularioDialog.vue'
import FilaService from '@/services/modules/FilaService'
import FilaFormularioModel from '@/models/entities/fila/FilaFormularioModel'

export default {
  components: {
    AtivarOuInativarAssociacaoDialog,
    ConfigurarAssociacaoFilaFormularioDialog,
  },

  emits: ['refetch-data'],

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    acessoDisable: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const isAtivarOuInativarAssociacaoFilaFormularioDialogOpen = ref(false)
    const loading = ref(false)
    const filaId = ref(0)
    const formularioId = ref(0)
    const isAssociacaoAtiva = ref(undefined)
    const isAssociacaoAtivaMensagem = ref('')

    function ativarOuInativarAssociacaoItem(item) {
      filaId.value = item.filaId
      formularioId.value = item.formularioId
      isAssociacaoAtiva.value = item.ativo
      isAssociacaoAtivaMensagem.value = item.ativo ? 'inativar' : 'ativar'
    }
    function openDialogAssociarOuDesassociar() {
      isAtivarOuInativarAssociacaoFilaFormularioDialogOpen.value = true
    }
    function closeDialogAssociarOuDesassociar() {
      isAtivarOuInativarAssociacaoFilaFormularioDialogOpen.value = false
    }
    async function confirmarAtivacaoOuInativacaoDaAssociacao() {
      if (loading.value) return
      try {
        loading.value = true
        await FilaService.ativarOuInativarAssociacaoEntreFilaEFormulario(filaId.value, formularioId.value, { ativar: !isAssociacaoAtiva.value })
        emit('refetch-data')
      } finally {
        closeDialogAssociarOuDesassociar()
        loading.value = false
      }
    }

    const isConfigurarAssociacaoFilaFormularioDialogOpen = ref(false)
    const associacaoSelecionada = ref(null)
    function selectAssociacao(item) {
      if (item.ativo === false) return
      associacaoSelecionada.value = new FilaFormularioModel(item)
      isConfigurarAssociacaoFilaFormularioDialogOpen.value = true
    }

    const ordensPermitidas = computed(() => Array.from({ length: props.items.filter(it => !!it.formularioId).length }, (_, index) => index + 1))

    const headers = [
      { text: 'NOME', value: 'nome' },
      { text: 'DESCRICAO', value: 'descricao' },
      { text: 'STATUS', value: 'ativo' },
      {
        text: 'OPÇÔES',
        value: 'actions',
        align: 'center',
      },
    ]

    return {
      isAtivarOuInativarAssociacaoFilaFormularioDialogOpen,
      loading,
      filaId,
      formularioId,
      isAssociacaoAtiva,
      isAssociacaoAtivaMensagem,
      ativarOuInativarAssociacaoItem,
      openDialogAssociarOuDesassociar,
      closeDialogAssociarOuDesassociar,
      confirmarAtivacaoOuInativacaoDaAssociacao,
      isConfigurarAssociacaoFilaFormularioDialogOpen,
      associacaoSelecionada,
      selectAssociacao,
      ordensPermitidas,
      headers,
      icons: {
        mdiCheckBold,
        mdiCloseThick,
        mdiDotsVertical,
      },
      cursorPointer: item => (item.ativo || item.ativo === null ? 'cursor-pointer' : ''),
    }
  },
}
</script>
